import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Trash2, Save } from 'lucide-react';
import TaskPriorityUrgency from './TaskPriorityUrgency';
import TaskGoalSelector from './TaskGoalSelector';
import TaskAssigneeSelector from './TaskAssigneeSelector';
import { DatePicker } from './DatePicker';

export interface TaskFormValues {
  title: string;
  description: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'not-important' | null;
  goalId?: string;
  assigneeIds?: string[];
}

interface TaskFormProps {
  initialValues: TaskFormValues;
  onSubmit: (values: TaskFormValues) => void;
  onCancel: () => void;
  submitButtonText: string;
  hideGoalSelector?: boolean;
  showAssignees?: boolean; // NEW: Default to false for clean separation
  isLoadingAssignees?: boolean;
  onDelete?: () => void;
  taskId?: string;
}

const TaskForm: React.FC<TaskFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  submitButtonText,
  hideGoalSelector = false,
  showAssignees = false, // NEW: Default to false for clean separation
  isLoadingAssignees = false,
  onDelete,
  taskId,
}) => {
  const [title, setTitle] = useState(initialValues.title);
  const [description, setDescription] = useState(initialValues.description);
  const [dueDate, setDueDate] = useState(initialValues.dueDate);
  const [priority, setPriority] = useState<'high' | 'medium' | 'not-important' | null>(initialValues.priority);
  const [goalId, setGoalId] = useState<string | null>(initialValues.goalId || null);
  const [assigneeIds, setAssigneeIds] = useState<string[]>(initialValues.assigneeIds || []);

  useEffect(() => {
    setTitle(initialValues.title);
    setDescription(initialValues.description);
    setDueDate(initialValues.dueDate);
    setPriority(initialValues.priority);
    setGoalId(initialValues.goalId || null);
    setAssigneeIds(initialValues.assigneeIds || []);
  }, [initialValues]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoadingAssignees) return; // Prevent submission while loading
    
    onSubmit({
      title: title.trim() || 'Untitled Task', // UPDATED: Default to "Untitled Task" if empty
      description,
      dueDate,
      priority,
      goalId,
      assigneeIds,
    });
  };

  const handlePriorityChange = (value: string) => {
    setPriority(value === 'none' ? null : value as 'high' | 'medium' | 'not-important' | null);
  };

  const handleGoalChange = (value: string | null) => {
    setGoalId(value);
  };

  const handleAssigneesChange = (newAssigneeIds: string[]) => {
    setAssigneeIds(newAssigneeIds);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter task title"
          maxLength={100}
          // REMOVED: required attribute to allow empty titles (defaults to "Untitled Task")
        />
      </div>

      {/* TEMPORARILY HIDDEN: Description field is temporarily hidden for CREATE modals only */}
      {/* To restore: Remove the conditional hiding logic and show for all cases */}
      {/* Edit modals (onDelete exists) still show description, Create modals (no onDelete) hide it */}
      <div className={`space-y-1 ${!onDelete ? 'hidden' : ''}`}>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter task description"
          className="min-h-[100px]"
        />
      </div>

      {/* Due Date and Priority - Side by side in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="dueDate">Due Date</Label>
          <DatePicker
            date={dueDate}
            setDate={setDueDate}
            hideLabel={true}
          />
        </div>
        
        <div className="space-y-1">
          <TaskPriorityUrgency
            priority={priority}
            onPriorityChange={handlePriorityChange}
          />
        </div>
      </div>

      {/* Assignees - Show above Project selector (only in project context) */}
      {showAssignees && (
        <TaskAssigneeSelector
          assigneeIds={assigneeIds}
          onAssigneesChange={handleAssigneesChange}
          isInitialLoading={isLoadingAssignees}
          taskId={taskId}
        />
      )}
      
      {/* Project Selector - Full width on its own line (if not hidden) */}
      {!hideGoalSelector && (
        <TaskGoalSelector
          goalId={goalId}
          onGoalChange={handleGoalChange}
        />
      )}

      <div className="flex justify-between items-center pt-4">
        {onDelete && (
          <Button
            type="button"
            variant="destructive"
            onClick={onDelete}
            className="flex items-center gap-2"
          >
            <Trash2 size={16} />
            Delete
          </Button>
        )}
        
        <div className={`flex gap-2 ${!onDelete ? 'w-full justify-end' : ''}`}>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex items-center gap-2 flex-1 sm:flex-none bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            disabled={isLoadingAssignees}
          >
            <Save size={16} />
            {submitButtonText}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default TaskForm;
